import React, { useEffect, useMemo, useState } from "react";
import { useTable, Column } from "react-table";
import axiosInstance from "../../api/axiosInstance";
import toast from "react-hot-toast";
import Pagination from "../ui/Pagination";

interface ErrorRecord {
  id: number;
  order_id: number;
  status: number;
  procesado: number;
  message: string;
  error_date: string;
  modification_date: string;
}

const ErrorsTable: React.FC = () => {
  const [errors, setErrors] = useState<ErrorRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchErrors = async (pageNumber: number, limit: number, search = '') => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axiosInstance.get(`/errors?page=${pageNumber}&limit=${limit}&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { data, meta } = response.data;
      console.log(`${data.length} registros en la página ${pageNumber} de ${meta.totalPages}`);

      const formattedErrors = data.map((error: ErrorRecord) => ({
        ...error,
        error_date: new Date(error.error_date).toLocaleString(),
        modification_date: new Date(error.modification_date).toLocaleString(),
      }));

      setErrors(formattedErrors);
      setTotalPages(meta.totalPages);
    } catch (error) {
      console.error("Error fetching error records:", error);
      toast.error("Error al cargar los datos.", { position: 'top-center' });
    }
  };

  useEffect(() => {
    fetchErrors(currentPage, pageSize, searchTerm);
  }, [currentPage, pageSize, searchTerm]);

  // ✅ Función con confirmación para actualizar el estado de Procesado
  const updateProcesado = async (orderId: number, newStatus: number) => {
    const confirmUpdate = await new Promise<boolean>((resolve) => {
      toast((t) => (
        <div className="flex flex-col">
          <p>¿Deseas marcar este pedido como {newStatus ? "procesado" : "no procesado"}?</p>
          <div className="flex justify-end space-x-2 mt-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              className="px-3 py-1 bg-green-500 text-white rounded"
            >
              Confirmar
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              className="px-3 py-1 bg-gray-300 rounded"
            >
              Cancelar
            </button>
          </div>
        </div>
      ), { duration: Infinity, position: 'top-center' });
    });

    if (!confirmUpdate) {
      toast((t) => (
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">Operación cancelada.</span>
        </div>
      ), { position: 'top-center' });
      return;
    }
    
    try {
      const token = localStorage.getItem("accessToken");
      await axiosInstance.patch(`/errors/${orderId}/procesado`,
        { procesado: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setErrors((prevErrors) =>
        prevErrors.map((error) =>
          error.order_id === orderId ? { ...error, procesado: newStatus } : error
        )
      );

      toast.success(`Estado actualizado: ${newStatus ? "Procesado" : "No procesado"}`, { position: 'top-center' });
    } catch (error) {
      console.error("Error al actualizar procesado:", error);
      toast.error("Error al actualizar el estado. Inténtalo nuevamente.", { position: 'top-center' });
    }
  };

  const columns: Column<ErrorRecord>[] = useMemo(
    () => [
      { Header: "#", accessor: "id" as const },
      { Header: "Pedido", accessor: "order_id" as const },
      { Header: "Status", accessor: "status" as const },
      { Header: "Mensaje", accessor: "message" as const },
      { Header: "Fecha de error", accessor: "error_date" as const },
      { Header: "Última Modificación", accessor: "modification_date" as const },
      { Header: "Procesado", accessor: "procesado" as const,
        Cell: ({ row }: { row: { original: ErrorRecord } }) => (
          <button
            className={`px-4 py-2 text-white rounded ${
              row.original.procesado === 1 ? "bg-green-500" : "bg-red-500"
            }`}
            onClick={() => updateProcesado(row.original.order_id, row.original.procesado === 1 ? 0 : 1)}>
            {row.original.procesado === 1 ? "Sí" : "No"}
          </button>
        ),},],
    []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns, data: errors,
  });

  return (
    <div className="overflow-x-auto">
      <table {...getTableProps()} className="w-full table-auto divide-y divide-gray-200 mb-16">
        <thead className="bg-gray-50">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={`headerGroup-${headerGroup.id}`}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  key={`header-${column.id}`} 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={`row-${row.original.id}`}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    key={`cell-${cell.column.id}-${cell.row.id}`}
                    className="px-6 py-4 break-words text-sm text-gray-500">
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </div>
  );
};

export default ErrorsTable;